/* Site chrome behaviour on every reader page (no framework; the page works
   without it: every destination is a plain link). Adds the theme toggle and
   theme modes, the apps and language menus ("System language" included),
   the phone menu drawer (a modal dialog with a focus trap), the pause switch
   for live updates, and confirms the pageview.
   Preferences: the pxke_theme cookie (on the parent domain, shared by the
   public hosts) with localStorage theme_mode as its per-host fallback,
   localStorage app_locale, and the pxke_locale cookie the server's chrome
   follows. Also rewrites every server-written
   age, date and stamp ([data-fmt] with data-epoch or a datetime) in the
   reader's clock and zone. */
(() => {
  'use strict'
  const doc = document
  const root = doc.documentElement
  const $ = (sel, el = doc) => el.querySelector(sel)
  const $$ = (sel, el = doc) => Array.from(el.querySelectorAll(sel))
  const LOCALES = ['en', 'es', 'ar', 'fa', 'fr', 'hi', 'ps', 'ru', 'zh']
  const THEME_COLOR = { light: '#EDF2EE', dark: '#0F1A1B' }

  const store = {
    get(key) {
      try { return localStorage.getItem(key) } catch { return null }
    },
    set(key, value) {
      try { localStorage.setItem(key, value) } catch (err) { console.warn(`[pxke] could not store ${key}:`, err) }
    },
    remove(key) {
      try { localStorage.removeItem(key) } catch (err) { console.warn(`[pxke] could not clear ${key}:`, err) }
    },
  }

  /* -- admin strip: the px_admin hint cookie (set by /admin/session at sign-in, cleared at
     sign-out) only decides whether to draw two links. It grants nothing: /admin checks the
     real session. Added here, not in the server's HTML, so the page cache stays shared. ---- */
  if (!doc.body.classList.contains('admin-body') && /(?:^|;\s*)px_admin=1(?:;|$)/.test(doc.cookie)) {
    const bar = doc.createElement('div')
    bar.className = 'pg-adminbar'
    bar.setAttribute('data-admin-bar', '')
    const link = (href, label) => {
      const a = doc.createElement('a')
      a.href = href
      a.textContent = label
      return a
    }
    bar.append(link('/admin', 'Admin'))
    const articleId = $('[data-article]')?.dataset.article
    if (articleId && /^[0-9a-f-]{8,64}$/i.test(articleId)) {
      bar.append(link(`/admin/articles/${articleId}`, 'Open in admin'))
    }
    const mast = $('[data-masthead]')
    if (mast) mast.before(bar)
    else doc.body.prepend(bar)
  }

  /* -- pageview confirmation: this browser ran the page, so the server counts
     the visit it held (a client that never runs scripts is not counted) ---- */
  if (!doc.body.classList.contains('admin-body')) {
    fetch('/api/v1/pageviews/confirm', { method: 'POST', credentials: 'include', keepalive: true })
      .catch((err) => console.warn('[pxke] pageview confirm failed:', err))
  }

  /* -- theme -------------------------------------------------------------
     The mode is the pxke_theme cookie ('light' | 'dark' | 'system'), which lives
     on the parent domain so every public host shares it; without it, the stored
     theme_mode key (absent = system). 'system' is stored too, so choosing it on
     one host also overrides an older per-host theme_mode on the others. The boot script reads the same two in the
     same order and always sets data-theme, so the attribute is never read back here.
     The masthead button cycles from the OS scheme: system -> opposite of the
     OS -> same as the OS -> system, so every click changes something visible. */
  const systemScheme = () => (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  const otherScheme = (s) => (s === 'dark' ? 'light' : 'dark')
  const themeMode = () => {
    const c = /(?:^|;\s*)pxke_theme=(light|dark|system)(?:;|$)/.exec(doc.cookie)
    const v = c ? c[1] : store.get('theme_mode')
    return v === 'light' || v === 'dark' ? v : 'system'
  }
  /* The mode the next click moves to. */
  const nextThemeMode = () => {
    const mode = themeMode()
    const os = systemScheme()
    if (mode === 'system') return otherScheme(os)
    if (mode === otherScheme(os)) return os
    return 'system'
  }
  const paintTheme = () => {
    const mode = themeMode()
    const resolved = mode === 'system' ? systemScheme() : mode
    root.dataset.theme = resolved
    for (const meta of $$('meta[name="theme-color"]')) meta.setAttribute('content', THEME_COLOR[resolved])
    syncThemeLabel()
  }
  /* The pxke_theme cookie string for a mode. Scoped to the parent domain when the
     server set data-pref-domain, so the choice follows the reader across the
     public hosts. */
  const themeCookie = (mode) => {
    const attrs = ['Path=/', 'Max-Age=31536000', 'SameSite=Lax']
    if (location.protocol === 'https:') attrs.push('Secure')
    if (root.dataset.prefDomain) attrs.push(`Domain=${root.dataset.prefDomain}`)
    return `pxke_theme=${mode}; ${attrs.join('; ')}`
  }
  const setThemeMode = (mode) => {
    if (mode === 'system') store.remove('theme_mode')
    else store.set('theme_mode', mode)
    doc.cookie = themeCookie(mode)
    paintTheme()
  }
  const themeBtn = $('[data-theme-toggle]')
  /* The button's label names the next mode; its icon shows the current one. */
  function syncThemeLabel() {
    if (!themeBtn) return
    const mode = themeMode()
    const next = nextThemeMode()
    const label = next === 'light' ? themeBtn.dataset.labelLight : next === 'dark' ? themeBtn.dataset.labelDark : themeBtn.dataset.labelSystem
    themeBtn.title = label
    themeBtn.setAttribute('aria-label', label)
    for (const ico of $$('[data-theme-ico]', themeBtn)) ico.hidden = ico.dataset.themeIco !== mode
  }
  themeBtn?.addEventListener('click', () => setThemeMode(nextThemeMode()))
  /* The OS scheme drives the system mode's attribute and the button's next step. */
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', paintTheme)
  syncThemeLabel()

  /* -- language ---------------------------------------------------------- */
  const systemLocale = () => {
    const nav = (navigator.language || 'en').toLowerCase()
    return LOCALES.find((code) => code !== 'en' && nav.startsWith(code)) || 'en'
  }
  const ARTICLE = /^\/news\/articles\/[^/]+$/
  const chromePath = (pathname) => {
    const m = /^\/([^/]+)(\/.*)?$/.exec(pathname)
    const rest = m && LOCALES.includes(m[1]) ? m[2] || '/' : pathname
    return rest.replace(/\/+$/, '') || '/'
  }
  const localeMenu = $('[data-locale-menu]')
  const translations = (localeMenu?.dataset.translations || '').split(',').filter(Boolean)
  const activeLocale = localeMenu?.dataset.active || 'en'
  /* Where a language goes: an article's translation (English = the bare URL), else this URL. */
  const localeTarget = (code) => {
    const rest = chromePath(location.pathname)
    let pathname = location.pathname
    if (ARTICLE.test(rest)) pathname = code !== 'en' && translations.includes(code) ? `/${code}${rest}` : rest
    return `${pathname}${location.search}${location.hash}`
  }
  const rememberLocale = (choice, resolved) => {
    store.set('app_locale', choice)
    const secure = location.protocol === 'https:' ? '; Secure' : ''
    doc.cookie = `pxke_locale=${resolved}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`
  }
  const chooseLocale = (choice) => {
    const resolved = choice === 'system' ? systemLocale() : choice
    rememberLocale(choice, resolved)
    const href = localeTarget(resolved)
    if (href === `${location.pathname}${location.search}${location.hash}`) {
      if (resolved !== activeLocale) location.reload()
      return
    }
    location.assign(href)
  }
  /* The ticked row: "System language" when nothing (or "system") is stored and it resolves to this page's locale. */
  const selectedLocale = () => {
    const stored = store.get('app_locale')
    return (stored == null || stored === 'system') && systemLocale() === activeLocale ? 'system' : activeLocale
  }
  /* The selected row's tick: a span appended once, removed when the row is no longer selected. */
  const setTick = (row, on) => {
    const tick = $('.tick', row)
    if (on && !tick) row.insertAdjacentHTML('beforeend', ' <span class="tick" aria-hidden="true">·</span>')
    else if (!on && tick) tick.remove()
  }
  const markSelected = (container) => {
    const selected = selectedLocale()
    for (const row of $$('[data-locale]', container)) {
      const on = row.dataset.locale === selected
      row.classList.toggle('selected', on)
      if (row.hasAttribute('aria-checked')) row.setAttribute('aria-checked', String(on))
      if (row.hasAttribute('aria-pressed')) row.setAttribute('aria-pressed', String(on))
      setTick(row, on)
    }
  }
  doc.addEventListener('click', (e) => {
    const row = e.target.closest?.('[data-locale]')
    if (!row || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    closeMenus()
    closeDrawer(false)
    chooseLocale(row.dataset.locale)
  })

  /* -- apps and language menus -------------------------------------------- */
  const toggles = $$('[data-popover-toggle]')
  function closeMenus(returnFocusTo) {
    for (const btn of toggles) {
      const pop = doc.getElementById(btn.dataset.popoverToggle)
      if (pop && !pop.hidden) {
        pop.hidden = true
        btn.setAttribute('aria-expanded', 'false')
      }
    }
    returnFocusTo?.focus()
  }
  for (const btn of toggles) {
    btn.addEventListener('click', (e) => {
      e.stopPropagation()
      const pop = doc.getElementById(btn.dataset.popoverToggle)
      if (!pop) return
      const open = pop.hidden
      closeMenus()
      if (open && pop.id === 'locale-menu') markSelected(pop)
      pop.hidden = !open
      btn.setAttribute('aria-expanded', String(open))
    })
  }
  doc.addEventListener('pointerdown', (e) => {
    if (!e.target.closest?.('.popover-wrap')) closeMenus()
  }, true)
  doc.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    const open = toggles.find((b) => b.getAttribute('aria-expanded') === 'true')
    if (open) closeMenus(open)
  })

  /* -- phone menu drawer: a modal dialog cloned from its <template> -------- */
  const menuBtn = $('button.menu-btn')
  const drawerTpl = doc.getElementById('site-drawer-template')
  let drawer = null
  let backdrop = null
  let saved = null
  const focusables = () => (drawer ? $$('a[href], button:not([disabled])', drawer) : [])
  function closeDrawer(returnFocus = true) {
    if (!drawer) return
    drawer.remove()
    backdrop?.remove()
    drawer = backdrop = null
    doc.body.style.overflow = saved.overflow
    doc.body.style.paddingRight = saved.pad
    menuBtn?.setAttribute('aria-expanded', 'false')
    if (returnFocus) menuBtn?.focus()
  }
  function openDrawer() {
    if (!drawerTpl || drawer) return
    const frag = drawerTpl.content.cloneNode(true)
    backdrop = $('.drawer-backdrop', frag)
    drawer = $('#site-drawer', frag)
    doc.body.append(frag)
    const mode = themeMode()
    for (const b of $$('[data-theme-mode]', drawer)) {
      const on = b.dataset.themeMode === mode
      b.classList.toggle('selected', on)
      b.setAttribute('aria-pressed', String(on))
      setTick(b, on)
    }
    markSelected(drawer)
    saved = { overflow: doc.body.style.overflow, pad: doc.body.style.paddingRight }
    const sb = innerWidth - root.clientWidth
    doc.body.style.overflow = 'hidden'
    if (sb > 0) doc.body.style.paddingRight = `${sb}px`
    menuBtn?.setAttribute('aria-expanded', 'true')
    backdrop?.addEventListener('click', () => closeDrawer())
    drawer.addEventListener('click', (e) => {
      const theme = e.target.closest('[data-theme-mode]')
      if (theme) {
        setThemeMode(theme.dataset.themeMode)
        for (const b of $$('[data-theme-mode]', drawer)) {
          const on = b === theme
          b.classList.toggle('selected', on)
          b.setAttribute('aria-pressed', String(on))
          setTick(b, on)
        }
        return
      }
      if (e.target.closest('a[href]')) closeDrawer(false)
    })
    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeDrawer()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      const active = doc.activeElement
      if (e.shiftKey && (active === first || !drawer.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    })
    focusables()[0]?.focus()
  }
  menuBtn?.addEventListener('click', (e) => {
    e.stopPropagation()
    closeMenus()
    if (drawer) closeDrawer()
    else openDrawer()
  })

  /* -- ages, dates and stamps in the reader's clock and zone ----------------
     The server writes every [data-fmt] element in UTC at render time, its
     instant in data-epoch (seconds) or a datetime attribute (ISO 8601 UTC);
     formats: when (story age), cover (cover age), date (calendar date with
     the year), day (calendar date with the year), stamp (date, then " · ",
     then HH:MM: "Oct 21, 2026 · 14:52"), clock (time of day "14:52"; no time
     zone). Swapped-in htmx fragments are rewritten too. */
  let strings = {}
  try {
    strings = JSON.parse($('#pxke-strings')?.textContent || '{}')
  } catch (err) {
    console.warn('[pxke] strings not readable:', err)
  }
  const tag = strings.locale || 'en'
  const numberFormat = new Intl.NumberFormat(tag)
  /* A count in the reader's digits and grouping (the server's format_count). */
  const formatNumber = (n) => numberFormat.format(n)
  /* The form of an ICU plural body for n: an exact =N form, else its CLDR category (Intl.PluralRules
     in the reader's language), else other. Mirrors i18n.py _pick_plural. */
  const pluralForm = (body, n) => {
    const forms = {}
    let i = 0
    while (i < body.length) {
      while (i < body.length && /\s/.test(body[i])) i++
      const head = /^(=\d+|zero|one|two|few|many|other)\s*\{/.exec(body.slice(i))
      if (!head) break
      i += head[0].length
      const start = i
      let depth = 1
      while (i < body.length && depth > 0) {
        if (body[i] === '{') depth++
        else if (body[i] === '}') depth--
        i++
      }
      forms[head[1]] = body.slice(start, i - 1)
    }
    const category = new Intl.PluralRules(tag).select(n)
    return forms[`=${n}`] ?? forms[category] ?? forms.other ?? ''
  }
  /* A message filled for a count: a plain "{count} ..." text, or an ICU
     "{count, plural, one {...} other {...}}" block (any number of them) whose form
     follows the reader's plural rules. The count itself is in the reader's digits. */
  const pluralText = (message, n) => {
    const text = String(message ?? '')
    const shown = formatNumber(n)
    const start = /\{(\w+),\s*plural,\s*/g
    let out = ''
    let pos = 0
    let m
    while ((m = start.exec(text)) !== null) {
      let depth = 1
      let end = start.lastIndex
      while (end < text.length && depth > 0) {
        if (text[end] === '{') depth++
        else if (text[end] === '}') depth--
        end++
      }
      const body = text.slice(start.lastIndex, end - 1)
      out += text.slice(pos, m.index) + pluralForm(body, n).split(`{${m[1]}}`).join(shown)
      pos = end
      start.lastIndex = end
    }
    return (out + text.slice(pos)).split('{count}').join(shown)
  }
  window.pxke = { formatNumber, pluralText }
  const fill = (key, count) => pluralText(strings[key] || '', count)
  const formats = {
    when(epoch, now) {
      const diff = now / 1000 - epoch
      if (diff < 60) return strings.timeJustNow
      if (diff < 3600) return fill('timeMinutesAgo', Math.floor(diff / 60))
      if (diff < 86400) return fill('timeHoursAgo', Math.floor(diff / 3600))
      if (diff < 86400 * 7) return fill('timeDaysAgo', Math.floor(diff / 86400))
      return new Date(epoch * 1000).toLocaleDateString(tag, { month: 'short', day: 'numeric' })
    },
    cover(epoch, now) {
      const diff = now / 1000 - epoch
      if (diff < 3600) return fill('timeMinutesAgo', Math.max(1, Math.floor(diff / 60)))
      if (diff < 86400) return fill('timeHoursAgo', Math.floor(diff / 3600))
      return new Date(epoch * 1000).toLocaleDateString(tag, { month: 'short', day: 'numeric', year: 'numeric' })
    },
    date(epoch) {
      return new Date(epoch * 1000).toLocaleDateString(tag, { month: 'short', day: 'numeric', year: 'numeric' })
    },
    day(epoch) {
      return new Date(epoch * 1000).toLocaleDateString(tag, { day: 'numeric', month: 'short', year: 'numeric' })
    },
    stamp(epoch) {
      return `\u2068${formats.date(epoch)} · ${formats.clock(epoch)}\u2069`
    },
    clock(epoch) {
      return new Date(epoch * 1000).toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit', hour12: false })
    },
  }
  const instant = (el) => {
    if (el.dataset.epoch) return Number(el.dataset.epoch)
    const iso = el.getAttribute('datetime')
    return iso ? Date.parse(iso) / 1000 : NaN
  }
  const localizeTimes = (scope = doc) => {
    if (!scope?.querySelectorAll) return
    const now = Date.now()
    const els = $$('[data-fmt]', scope)
    if (scope.matches?.('[data-fmt]')) els.push(scope)
    for (const el of els) {
      try {
        const fmt = formats[el.dataset.fmt]
        const epoch = instant(el)
        if (!fmt || !Number.isFinite(epoch) || !epoch) continue
        const text = fmt(epoch, now)
        if (text) el.textContent = text
      } catch (err) {
        console.warn('[pxke] date not localized:', err)
      }
    }
  }
  localizeTimes()
  doc.addEventListener('htmx:afterSwap', (e) => localizeTimes(e.target))
  doc.addEventListener('htmx:load', (e) => localizeTimes(e.target))

  /* -- live bands ([data-live]: chain readings, sparkline, markets strip) ----
     No polling while the tab is hidden (htmx runs with allowEval off, so the
     request is cancelled here); one refresh when the tab shows again. */
  doc.addEventListener('htmx:beforeRequest', (e) => {
    if (doc.hidden && e.target.closest?.('[data-live]')) e.preventDefault()
  })
  doc.addEventListener('visibilitychange', () => {
    if (doc.hidden || !window.htmx) return
    for (const el of doc.querySelectorAll('[data-live]')) htmx.trigger(el, 'pxke:refresh')
  })

  /* -- live updates: pause switch -------------------------------------------
     [data-motion-toggle] flips <html data-motion="paused"> (CSS stops the
     animations) and the stored choice in localStorage pxke_motion. With no
     stored choice, prefers-reduced-motion: reduce starts paused. While paused,
     htmx requests from [data-live] elements are cancelled. */
  const MOTION_KEY = 'pxke_motion'
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
  const paintMotion = (paused) => {
    if (paused) root.dataset.motion = 'paused'
    else delete root.dataset.motion
    for (const btn of $$('[data-motion-toggle]')) {
      const text = paused ? btn.dataset.labelResume : btn.dataset.labelPause
      const label = $('[data-motion-label]', btn) || btn
      label.textContent = text
      btn.title = text
    }
  }
  const stored = store.get(MOTION_KEY)
  paintMotion(stored == null ? reducedMotion.matches : stored === 'paused')
  doc.addEventListener('click', (e) => {
    if (!e.target.closest?.('[data-motion-toggle]')) return
    const paused = root.dataset.motion !== 'paused'
    store.set(MOTION_KEY, paused ? 'paused' : 'running')
    paintMotion(paused)
  })
  doc.addEventListener('htmx:beforeRequest', (e) => {
    if (root.dataset.motion === 'paused' && e.target.closest?.('[data-live]')) e.preventDefault()
  })
  /* A swapped-in live band carries a fresh pause button: paint it in the page's state. */
  doc.addEventListener('htmx:afterSwap', () => paintMotion(root.dataset.motion === 'paused'))

  /* -- The markets strip keeps one row: the least important tiles (fit-1 first, then fit-2 …) hide
     only while the row still overflows, so a wide screen shows every tile in any language. */
  const FIT_ORDER = ['fit-1', 'fit-2', 'fit-3', 'fit-4']
  const fitStrip = () => {
    for (const tiles of $$('.px-live-tiles')) {
      tiles.classList.add('is-fitted')
      for (const el of tiles.children) el.hidden = false
      if (tiles.scrollWidth <= tiles.clientWidth) continue
      for (const cls of FIT_ORDER) {
        for (const el of $$(`:scope > .${cls}`, tiles)) el.hidden = true
        if (tiles.scrollWidth <= tiles.clientWidth) break
      }
    }
  }
  fitStrip()
  if ('ResizeObserver' in window) {
    const strip = $('.px-liveline')
    if (strip) new ResizeObserver(() => fitStrip()).observe(strip)
  } else {
    window.addEventListener('resize', fitStrip)
  }
  doc.addEventListener('htmx:afterSwap', (e) => {
    if (e.target.id === 'markets' || e.target.querySelector?.('.px-live-tiles')) fitStrip()
  })
  doc.fonts?.ready.then(fitStrip)
})()
